/**
 * CatalogSection: the Employees page's proxy catalog (TRA-1507). Every
 * base model teams can call, grouped by the public upstream that serves
 * it, with add, edit, deprecate and reactivate. Each change reaches the
 * gateway as it is saved; there is no YAML and no deploy.
 */

import { useState } from 'react'
import { Plus, Shield } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import type { CatalogModelView } from '@/api/generated/model'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useCatalog,
  useCatalogImpact,
  useDeprecateCatalogModel,
  useReactivateCatalogModel,
} from '@/hooks/useCatalog'

import {
  formatPrice,
  formatReadsImages,
  formatReasoningEffort,
  groupByUpstream,
  type UpstreamGroup,
} from './catalog'
import { CatalogModelDialog } from './CatalogModelDialog'

interface ModelRowProps {
  model: CatalogModelView
  onEdit: (model: CatalogModelView) => void
  onDeprecate: (model: CatalogModelView) => void
  onReactivate: (model: CatalogModelView) => void
}

function ModelRow({ model, onEdit, onDeprecate, onReactivate }: ModelRowProps): React.JSX.Element {
  const deprecated = model.deprecated_at !== null
  return (
    <TableRow className={deprecated ? 'text-muted-foreground' : undefined}>
      <TableCell className="font-mono text-xs">
        {model.slug}
        {deprecated && (
          <Badge variant="secondary" className="ml-2">
            Deprecated
          </Badge>
        )}
      </TableCell>
      <TableCell>{model.display_name}</TableCell>
      <TableCell className="font-mono text-xs">{model.provider_model}</TableCell>
      <TableCell className="text-right font-mono text-xs tabular-nums">
        {formatPrice(model.pricing?.input_per_m)}
      </TableCell>
      <TableCell className="text-right font-mono text-xs tabular-nums">
        {formatPrice(model.pricing?.cached_per_m)}
      </TableCell>
      <TableCell className="text-right font-mono text-xs tabular-nums">
        {formatPrice(model.pricing?.output_per_m)}
      </TableCell>
      <TableCell className="font-mono text-xs">{formatReasoningEffort(model)}</TableCell>
      <TableCell>{formatReadsImages(model)}</TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label={`Edit ${model.slug}`}
            onClick={() => {
              onEdit(model)
            }}
          >
            Edit
          </Button>
          {deprecated ? (
            <Button
              variant="outline"
              size="sm"
              aria-label={`Reactivate ${model.slug}`}
              onClick={() => {
                onReactivate(model)
              }}
            >
              Reactivate
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              aria-label={`Deprecate ${model.slug}`}
              onClick={() => {
                onDeprecate(model)
              }}
            >
              Deprecate
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

interface DeprecateDialogProps {
  model: CatalogModelView
  onClose: () => void
}

/**
 * The impact check before a deprecation: the user models that route to
 * the slug. With any, the confirm evicts their routes as well; the
 * button says so and the API refuses the request without that flag.
 */
function DeprecateDialog({ model, onClose }: DeprecateDialogProps): React.JSX.Element {
  const impact = useCatalogImpact(model.id)
  const deprecate = useDeprecateCatalogModel()
  const [error, setError] = useState<string | null>(null)
  const impacted = impact.data?.models ?? []

  return (
    <ConfirmDialog
      open
      onOpenChange={open => {
        if (!open) onClose()
      }}
      title={`Deprecate ${model.slug}`}
      description={
        impact.isLoading ? (
          'Checking which user models route to this slug.'
        ) : impact.isError ? (
          'The impact check failed. Close and try again.'
        ) : impacted.length === 0 ? (
          'No user models route to this slug. Calls to it will return 404 once it is deprecated.'
        ) : (
          <span className="block space-y-2">
            <span className="block">
              These user models route to this slug and will lose their backing
              model. Confirming also removes their routes, and their aliases,
              from the gateway.
            </span>
            <span className="block font-mono text-xs">
              {impacted.map(hit => (
                <span key={`${hit.team_id}/${hit.model_slug}`} className="block">
                  {hit.team_slug ?? hit.team_id}/{hit.model_slug}
                  {hit.aliases.length > 0 && ` (aliases: ${hit.aliases.join(', ')})`}
                </span>
              ))}
            </span>
          </span>
        )
      }
      confirmLabel={
        impacted.length === 0
          ? 'Deprecate'
          : `Deprecate and evict ${String(impacted.length)} route${impacted.length === 1 ? '' : 's'}`
      }
      pendingLabel="Deprecating"
      isPending={deprecate.isPending}
      destructive
      confirmDisabled={!impact.isSuccess}
      error={error}
      onConfirm={() => {
        deprecate.mutate(
          { id: model.id, cascade: impacted.length > 0 },
          {
            onSuccess: () => {
              toast.success(`${model.slug} deprecated.`)
              onClose()
            },
            onError: err => {
              setError(getApiErrorDetail(err) ?? 'The deprecation failed.')
            },
          },
        )
      }}
    />
  )
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; model: CatalogModelView }
  | { kind: 'deprecate'; model: CatalogModelView }

export function CatalogSection(): React.JSX.Element {
  const catalog = useCatalog()
  const reactivate = useReactivateCatalogModel()
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const close = (): void => {
    setDialog({ kind: 'closed' })
  }

  const onReactivate = (model: CatalogModelView): void => {
    reactivate.mutate(model.id, {
      onSuccess: () => {
        toast.success(`${model.slug} is callable again.`)
      },
      onError: err => {
        toast.error(getApiErrorDetail(err) ?? 'The reactivation failed.')
      },
    })
  }

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-lg font-medium text-foreground">Catalog</h2>
          <p className="text-sm text-muted-foreground">
            The base models every team can call. A change reaches the gateway
            when you save it. Adding a provider still needs its key on the
            gateway worker.
          </p>
        </div>
        <Button
          disabled={!catalog.isSuccess}
          onClick={() => {
            setDialog({ kind: 'add' })
          }}
        >
          <Plus className="h-4 w-4" />
          Add model
        </Button>
      </div>

      {catalog.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading catalog…</p>
      ) : catalog.isError ? (
        <EmptyState
          icon={Shield}
          title="Couldn’t load the catalog"
          description={getApiErrorDetail(catalog.error) ?? 'Try refreshing.'}
        />
      ) : catalog.data ? (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Slug</TableHead>
                <TableHead>Display name</TableHead>
                <TableHead>Provider model</TableHead>
                <TableHead className="whitespace-nowrap text-right">Input $/1M</TableHead>
                <TableHead className="whitespace-nowrap text-right">Cached $/1M</TableHead>
                <TableHead className="whitespace-nowrap text-right">Output $/1M</TableHead>
                <TableHead className="whitespace-nowrap">Reasoning effort</TableHead>
                <TableHead className="whitespace-nowrap">Reads images</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupByUpstream(catalog.data).map(group => (
                <UpstreamRows
                  key={group.upstream.id}
                  group={group}
                  onEdit={model => {
                    setDialog({ kind: 'edit', model })
                  }}
                  onDeprecate={model => {
                    setDialog({ kind: 'deprecate', model })
                  }}
                  onReactivate={onReactivate}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      {(dialog.kind === 'add' || dialog.kind === 'edit') && catalog.data && (
        <CatalogModelDialog
          open
          onOpenChange={open => {
            if (!open) close()
          }}
          upstreams={catalog.data.upstreams}
          model={dialog.kind === 'edit' ? dialog.model : null}
        />
      )}
      {dialog.kind === 'deprecate' && <DeprecateDialog model={dialog.model} onClose={close} />}
    </section>
  )
}

interface UpstreamRowsProps extends Omit<ModelRowProps, 'model'> {
  group: UpstreamGroup
}

function UpstreamRows({ group, ...actions }: UpstreamRowsProps): React.JSX.Element {
  return (
    <>
      <TableRow className="bg-muted/40 hover:bg-muted/40">
        <TableCell colSpan={9} className="py-2">
          <span className="text-sm font-medium text-foreground">{group.upstream.name}</span>
          <span className="ml-2 font-mono text-xs text-muted-foreground">
            {group.upstream.id}
            {group.upstream.base_url && ` · ${group.upstream.base_url}`}
          </span>
        </TableCell>
      </TableRow>
      {group.models.length === 0 ? (
        <TableRow>
          <TableCell colSpan={9} className="text-sm text-muted-foreground">
            No models on this upstream.
          </TableCell>
        </TableRow>
      ) : (
        group.models.map(model => <ModelRow key={model.id} model={model} {...actions} />)
      )}
    </>
  )
}
